// V1, Maximum density (2026-10-04, GUI audit docs/GUI_REDESIGN_AUDIT_2026_10_04.md): one screen, no page scroll.
// Left: the MAIN blotter with stop status. Centre: equity, the selected position, and a lower tab strip (signals, orders,
// Tier 2). Right: the grouped alert box, risk, engine and data health. Real data only; where the API does not expose a
// field yet (decision price, fill, parameter version, breakers) the screen says so instead of inventing a value.
import React, { useState, useMemo } from 'react'
import SystemAlerts from '../components/SystemAlerts'
import useDeskData from '../lib/useDeskData'
import { Panel, KV, PositionsTable, RiskPanel, EnginePanel, EquityChart, SignalsList, OrdersList, MONO, mainRows, tier2Rows, num, signed } from '../components/deskParts'
import { timeAgo } from '../lib/utils'

export default function DenseDesk() {
  const d = useDeskData({ pollMs: 20_000 })
  const [sel, setSel] = useState(null)
  const [tab, setTab] = useState('signals')
  const rows = useMemo(() => mainRows(d.main), [d.main])
  const picked = rows.find(r => r.symbol === sel) || rows[0] || null
  const s = d.main?.summary

  return (
    <div className="v1-wrap h-full flex flex-col gap-2 min-h-0">
      <div className="flex items-center gap-3 flex-shrink-0" style={{ ...MONO, fontSize: '0.66rem', color: '#64748b' }}>
        <span>V1 · MAXIMUM DENSITY</span>
        <span>{d.asOf ? `updated ${timeAgo(d.asOf)} · auto 20s` : 'loading…'}</span>
        {d.errors.length > 0 && <span style={{ color: '#fca5a5' }}>stale: {d.errors.join(', ')}</span>}
        <span className="flex-1" />
        <button onClick={d.reload} style={{ ...MONO, border: '1px solid #374151', borderRadius: 3, padding: '1px 8px', color: '#94a3b8' }}>REFRESH</button>
      </div>

      <div className="v1-alerts-top"><SystemAlerts health={d.health} schwabStatus={d.schwab} wsStatus={d.ws} dbAlerts={d.alerts} onAckGroup={d.ackGroup} mainSummary={d.main?.summary} /></div>

      <div className="v1-grid">
        <Panel title={`MAIN positions · ${s ? s.count : '…'}`} bodyStyle={{ padding: 0 }} className="v1-blotter"
          right={s && s.unprotected_count > 0 && <span style={{ color: '#fca5a5', fontWeight: 700 }}>{s.unprotected_count} NO STOP</span>}>
          <PositionsTable rows={rows} selected={picked?.symbol} onSelect={setSel} />
        </Panel>

        <div className="min-h-0 min-w-0 flex flex-col gap-2">
          <Panel title={`Equity · ${d.selectedAccount === 'main' ? 'MAIN' : 'TIER 2'}`} style={{ flexShrink: 0 }}>
            <EquityChart data={d.equity} period={d.period} onPeriod={d.setPeriod} height={150} />
          </Panel>
          <Panel title={picked ? `${picked.symbol} · detail` : 'detail'} style={{ flexShrink: 0 }}>
            {picked ? (
              <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <KV rows={[['entry', num(picked.entry)], ['last', num(picked.last)], ['value', `$${num(picked.value, 0)}`], ['qty', num(picked.qty, 0)]]} />
                <KV rows={[['P&L $', signed(picked.pnl)], ['P&L %', signed(picked.pnl_pct)], ['held', picked.held_days == null ? '—' : `${picked.held_days.toFixed(1)} d`],
                  ['stop', picked.has_stop ? `✓ ${num(picked.stop_price)}` : 'NONE', picked.has_stop ? 'good' : 'bad']]} />
                <p style={{ ...MONO, fontSize: '0.64rem', color: '#64748b', lineHeight: 1.5 }}>
                  decision price, fill, parameter version: not exposed by the API yet (T-055, T-069). Click a row to change the symbol.
                </p>
              </div>
            ) : <p style={{ ...MONO, fontSize: '0.68rem', color: '#64748b' }}>no MAIN positions loaded</p>}
          </Panel>
          <Panel grow className="v1-center-tabs" title={
            <span className="flex gap-1">
              {[['signals', 'Signals'], ['orders', 'Orders'], ['tier2', 'Tier 2']].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)}
                  style={{ ...MONO, fontSize: '0.62rem', padding: '0 7px', borderRadius: 3, border: `1px solid ${tab === k ? '#0e7490' : 'transparent'}`, color: tab === k ? '#67e8f9' : '#64748b', background: tab === k ? 'rgba(8,145,178,0.12)' : 'transparent' }}>
                  {label.toUpperCase()}
                </button>
              ))}
            </span>
          } bodyStyle={{ padding: 0 }}>
            {tab === 'signals' && <SignalsList signals={d.signals} />}
            {tab === 'orders' && <OrdersList orders={d.orders} />}
            {tab === 'tier2' && <PositionsTable rows={tier2Rows(d.tier2)} showBook />}
          </Panel>
        </div>

        <div className="v1-right min-h-0 min-w-0 flex flex-col gap-2 overflow-auto">
          <div className="v1-alerts-side"><SystemAlerts health={d.health} schwabStatus={d.schwab} wsStatus={d.ws} dbAlerts={d.alerts} onAckGroup={d.ackGroup} mainSummary={d.main?.summary} /></div>
          <Panel title="Risk" style={{ flexShrink: 0 }}><RiskPanel account={d.account} main={d.main} tier2={d.tier2} /></Panel>
          <Panel title="Engine & data" style={{ flexShrink: 0 }}><EnginePanel health={d.health} ws={d.ws} /></Panel>
        </div>
      </div>
    </div>
  )
}
